import { gridSize } from '@atlaskit/theme/constants';
import { type Size } from '@atlaskit/icon';
import { type ConfluenceLicenseStatusList, CONFTREE_CONNECT_SPACE } from '../../state/ui/types';
import { type Template, TemplateType } from '../../containers/template/types';
import {
	getDefaultTemplates,
	getBasePreExpandTemplates,
	moreTemplatesBase,
	getTutorialTemplate,
} from './constants';
import { isMoreTemplates } from '../../containers/template/utils';
import messages from './messages';
import { type BlueprintData } from '../../../src/state/ui/blueprints/types';
import { type IntlShape } from 'react-intl-next';

export const getIconSize = (size: Size): number => gridSize() * (size === 'small' ? 3 : 4);

export const getIconImageSize = (size: Size): number => (size === 'small' ? 14 : 18);

export const getDateString = (): string => {
	const dt = new Date();
	const month = (dt.getMonth() + 1).toString().padStart(2, '0');
	const day = dt.getDate().toString().padStart(2, '0');
	return `${dt.getFullYear()}-${month}-${day}`;
};

export const getPostExpandTemplates = (
	spaceKey: string | null,
	areTemplatesEnabled: boolean,
	confluenceLicenseStatus: ConfluenceLicenseStatusList,
	blueprints: BlueprintData[],
	intl: IntlShape,
	isConfluenceAutoProvisioned?: boolean,
	isProjectPagesSporkRebrandingEnabled?: boolean,
): Template[] => {
	const moreTemplates: Template = {
		...moreTemplatesBase,
		type: TemplateType.Link,
		name: messages.templatesPostExpandMore,
		url: `/wiki/templates?space=${spaceKey}`,
		renderShortcutIcon: true,
	};

	// Verify that we should only obtain templates from defaultTemplates that also exist in the blueprints api
	const enabledTemplates = getDefaultTemplates().filter((template) =>
		blueprints.find(
			(blueprint) =>
				blueprint.itemModuleCompleteKey &&
				blueprint.itemModuleCompleteKey === template.itemModuleCompleteKey,
		),
	);

	const allTemplates = [...enabledTemplates, moreTemplates];

	if (isConfluenceAutoProvisioned) {
		allTemplates.unshift(getTutorialTemplate(intl, isProjectPagesSporkRebrandingEnabled));
	}

	return allTemplates.map((tmpl) => ({
		...tmpl,
		tooltip: getTooltip(tmpl, areTemplatesEnabled, confluenceLicenseStatus),
	}));
};

export const getPreExpandTemplates = (
	intl: IntlShape,
	isConfluenceAutoProvisioned?: boolean,
	isProjectPagesSporkRebrandingEnabled?: boolean,
) => {
	if (isConfluenceAutoProvisioned) {
		return [
			getTutorialTemplate(intl, isProjectPagesSporkRebrandingEnabled),
			...getBasePreExpandTemplates(),
		];
	}

	return getBasePreExpandTemplates();
};

const getTooltip = (
	tmpl: Template,
	areTemplatesEnabled: boolean,
	confluenceLicenseStatus: ConfluenceLicenseStatusList,
) => {
	if (confluenceLicenseStatus === CONFTREE_CONNECT_SPACE) {
		return messages.connectSpaceTooltip;
	} else if (!areTemplatesEnabled) {
		return undefined;
	} else if (isMoreTemplates(tmpl)) {
		return messages.templatesPostExpandMoreTooltip;
	} else {
		return undefined;
	}
};
